import type { PortfolioConnectionPublic } from '@workspace/types';

export type MissingPhoneDebtSample = {
  externalRef: string;
  name: string;
};

export type MissingPhoneDebtStats = {
  count: number;
  sample: MissingPhoneDebtSample[];
};

const MAX_SAMPLE = 200;

function asSampleRow(row: unknown): MissingPhoneDebtSample | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const record = row as { externalRef?: unknown; name?: unknown };
  const externalRef = String(record.externalRef ?? '').trim().slice(0, 64);
  if (!externalRef) {
    return null;
  }
  const name = String(record.name ?? '').trim().slice(0, 80);
  return { externalRef, name: name || externalRef };
}

export function parseMissingPhoneDebtStats(
  raw: Record<string, unknown> | null | undefined,
): MissingPhoneDebtStats | null {
  if (!raw) {
    return null;
  }
  const count = Number(raw.missingPhoneWithDebtCount);
  if (!Number.isFinite(count) || count <= 0) {
    return null;
  }
  const sample: MissingPhoneDebtSample[] = [];
  const rows = raw.missingPhoneWithDebtSample;
  if (Array.isArray(rows)) {
    for (const row of rows.slice(0, MAX_SAMPLE)) {
      const parsed = asSampleRow(row);
      if (parsed) {
        sample.push(parsed);
      }
    }
  }
  return { count: Math.floor(count), sample };
}

export type CloseHeldStats = { open: number; wouldClose: number };

/**
 * Cierre retenido en la ultima sync: Setup dejo de informar buena parte de la cartera y el
 * sistema no cerro esas deudas hasta que alguien confirme que es correcto.
 */
export function parseCloseHeld(raw: Record<string, unknown> | null | undefined): CloseHeldStats | null {
  const held = raw?.closeHeld;
  if (!held || typeof held !== 'object' || Array.isArray(held)) {
    return null;
  }
  const record = held as { open?: unknown; wouldClose?: unknown };
  const open = Number(record.open);
  const wouldClose = Number(record.wouldClose);
  if (!Number.isFinite(open) || !Number.isFinite(wouldClose) || wouldClose <= 0) {
    return null;
  }
  return { open: Math.floor(open), wouldClose: Math.floor(wouldClose) };
}

export function parseMissingEmailDebtCount(raw: Record<string, unknown> | null | undefined): number {
  if (!raw) {
    return 0;
  }
  const count = Number(raw.missingEmailWithDebtCount);
  if (!Number.isFinite(count) || count <= 0) {
    return 0;
  }
  return Math.floor(count);
}

export function pickLatestMissingPhoneStats(
  connections: PortfolioConnectionPublic[],
): Record<string, unknown> | null {
  let latestAt = '';
  let latestStats: Record<string, unknown> | null = null;
  for (const connection of connections.slice(0, 50)) {
    const stats = connection.lastSyncStats;
    if (!parseMissingPhoneDebtStats(stats) || !stats) {
      continue;
    }
    const at = connection.lastSyncAt ?? '';
    if (!latestStats || at > latestAt) {
      latestAt = at;
      latestStats = stats;
    }
  }
  return latestStats;
}
